<template>
  <div ref="tooltipRef" class="flex min-w-0 items-center">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { type Placement } from 'tippy.js';
import { onUnmounted, ref, watch } from 'vue';
import { useTippy } from 'vue-tippy';
import { TOOLTIP_Z_INDEX } from './layering/layering';

const tooltipRef = ref<Element>();
const tippyInstance = ref<ReturnType<typeof useTippy> | null>(null);

const props = withDefaults(
  defineProps<{
    content?: string | number | undefined | null;
    maxWidth?: number;
    placement?: Placement;
    /**
     * Whether the tooltip box itself can be hovered/interacted with.
     * Set to `false` for tooltips anchored to elements with their own hover
     * styling (e.g. table cells), so the tooltip's pointer-events don't
     * cause that hover styling to flicker off.
     */
    interactive?: boolean;
  }>(),
  {
    interactive: true,
    maxWidth: 192,
  },
);

watch(
  () => props.content,
  (content) => {
    // Destroy existing tooltip instance if it exists
    if (tippyInstance.value) {
      tippyInstance.value.destroy();
      tippyInstance.value = null;
    }

    // Create new tooltip only if content is provided
    if (content) {
      tippyInstance.value = useTippy(tooltipRef, {
        aria: {
          // `null` when interactive: true, otherwise "describedby"
          content: 'auto',
          // matches `interactive` boolean
          expanded: 'auto',
        },
        content: props.content as string,
        placement: props.placement ?? 'bottom',
        arrow: false,
        followCursor: false,
        maxWidth: props.maxWidth,
        theme: 'teachino-tippy',
        zIndex: TOOLTIP_Z_INDEX,
        onShow: (instance): void => {
          // Body-mounted tooltips must clear their trigger's layer, including
          // floating assistants and dialogs above them, without covering sibling menus.
          let zIndex = TOOLTIP_Z_INDEX;
          let element: Element | null = instance.reference;
          while (element) {
            const ancestorLayer = Number.parseInt(
              getComputedStyle(element).zIndex,
              10,
            );
            if (Number.isFinite(ancestorLayer))
              zIndex = Math.max(zIndex, ancestorLayer + 1);
            element = element.parentElement;
          }
          instance.setProps({ zIndex });
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        allowHTML: true,
        interactive: props.interactive,
        interactiveBorder: 0,
        duration: 0,
        trigger: 'mouseenter focus',
        appendTo: () => document.body,
      });
    }
  },
  { immediate: true },
);

// Cleanup on component unmount
onUnmounted(() => {
  if (tippyInstance.value) {
    tippyInstance.value.destroy();
    tippyInstance.value = null;
  }
});
</script>

<style scoped>
* >>> .tippy-box[data-theme~='teachino-tippy'] {
  background-color: var(--color-secondary-700);
  border-radius: 12px !important;

  color: var(--color-secondary-200);
}
</style>
